import { useState, useEffect } from 'react';
import { SchoolProps } from '../types/SchoolProps';
import { SchoolListing } from './SchoolListing';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow
} from '@vis.gl/react-google-maps';
import { getSchoolcon } from '../utils/getSchoolcon';
import { SchoolMapProps } from '../types/SchoolMapProps';
import { MapCameraChangedEvent } from '@vis.gl/react-google-maps';

export const SchoolMap = ({
  schools,
  cameraState,
  onCameraChanged,
  onEnquire,
  pinLocation,
  search
}: SchoolMapProps) => {
  const [infoWindowOpen, setInfoWindowOpen] = useState<string>('');

  const [center, setCenter] = useState<{ lat: number; lng: number }>({ lat: -37.8136, lng: 144.9631 });
  const [zoom, setZoom] = useState<number>(10);
  const [pinInfoWindowOpen, setPinInfoWindowOpen] = useState<boolean>(false);

  // get the average of all the lat and longs

  useEffect(() => {
    if (schools.length > 0) {
      const validSchools = schools.filter((school: SchoolProps) => school.lat && school.long);
      if (validSchools.length === 0) {
        setCenter({ lat: -37.825567357057054, lng: 144.95606987987998 })
        setZoom(10);
        return;
      }
      const latSum = validSchools.reduce((acc: number, school: SchoolProps) => {
        return acc + (school.lat);
      }, 0);
      const longSum = validSchools.reduce((acc: number, school: SchoolProps) => {
        return acc + (school.long);
      }, 0);
      setCenter({ lat: latSum / validSchools.length, lng: longSum / validSchools.length });
      setCenter((prevCenter) => ({ lat: prevCenter.lat, lng: prevCenter.lng }));
    }
    setZoom(10);
    if (schools.length <= 20) {
      setZoom(12);
    }
    if (schools.length <= 5) {
      setZoom(13);
    }

  }, [schools]);

  const handleCameraChange = (ev: MapCameraChangedEvent) => {
    if (ev.detail.center) {
      setCenter(ev.detail.center);
    }
    if (ev.detail.zoom) {
      setZoom(ev.detail.zoom);
    }

    onCameraChanged(ev);

  };

  return process.env.REACT_APP_GOOGLE_MAPS_API_KEY && process.env.REACT_APP_GOOGLE_MAPS_MAP_ID ? (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Map
        id='map'
        style={{ width: '100vw', height: '100vh' }}
        defaultCenter={center}
        defaultZoom={zoom}
        center={center}
        zoom={zoom}
        gestureHandling={'greedy'}
        disableDefaultUI={false}
        mapTypeControl={false}
        fullscreenControl={false}
        streetViewControl={false}
        clickableIcons={false}
        mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID}
        reuseMaps={true}
        onCameraChanged={handleCameraChange}>
        {schools && schools.map(
          (school: SchoolProps, index: number) =>
            school.lat &&
            school.long && (
              <div key={index}>
                <AdvancedMarker
                  key={school.eno}
                  onClick={() => setInfoWindowOpen(school.eno)}
                  position={{ lat: school.lat, lng: school.long }}
                  anchorPoint={['100%', '100%']}>
                  {getSchoolcon(school.school_level)}
                </AdvancedMarker>
                {infoWindowOpen === school.eno && (

                  <div>

                    {/* // Note the CSS within App.css will dynamically hide the Info Window on small devices */}
                    <InfoWindow
                      key={school.eno}
                      position={{ lat: school.lat, lng: school.long }}
                      pixelOffset={[0, -30]}
                      onCloseClick={() => setInfoWindowOpen('')}
                      className='info-window'>
                      <SchoolListing
                        school={school}
                        listingType='map'
                        closeInfo={() => setInfoWindowOpen('')}
                        onEnquire={() => { onEnquire(school) }}
                      />
                    </InfoWindow>

                    <div className='sm-info-window md:hidden max-md:visible max-md:fixed max-md:bottom-0 max-md:left-0 max-md:p-5 max-md:w-full'>
                      <SchoolListing
                        school={school}
                        listingType='map'
                        closeInfo={() => setInfoWindowOpen('')}
                        onEnquire={() => { onEnquire(school) }}
                        className='max-md:w-full'
                      />
                    </div>
                  </div>

                )}
              </div>
            )
        )}
        {pinLocation && (
          <div className="info-window-container">
            <AdvancedMarker
              position={{ lat: pinLocation.lat, lng: pinLocation.lng }}
              anchorPoint={['100%', '100%']}
              onClick={() => setPinInfoWindowOpen(!pinInfoWindowOpen)}
            >
              <img src='https://maps.google.com/mapfiles/ms/icons/red-dot.png' alt='pin' />
            </AdvancedMarker>
            {pinInfoWindowOpen && <InfoWindow
              position={{ lat: pinLocation.lat, lng: pinLocation.lng }}
              pixelOffset={[-15, -40]}
              className='info-window'>
              <div className='bg-white p-2 rounded-md'>Showing schools near {search}</div>
            </InfoWindow>}
          </div>
        )}
      </Map>
    </APIProvider>
  ) : <div>Please provide a google maps API key and map ID</div>;
};
